/**
 * useRealizationsData — les pages d'« Activités terminées » (patron A).
 *
 * Le legacy choisit son service sur l'élève visé (`RealizationsTabScreen.js:70-78`) :
 * `getStudentFinishedActivities` quand la route en porte un, `getFinishedActivities` sinon. Les
 * deux atterrissent dans `currentUser.history.activities` — page 1 remplace, les suivantes
 * concatènent (`currentUser.js:1273-1300`) —, donc seul le curseur vit ici.
 *
 * `params` porte la recherche (`useFinishedActivitiesParams`) : chaque page la renvoie, et la
 * changer recharge la page 1.
 */
import { useCallback, useEffect, useRef, useState } from "react"
import { useDispatch, useSelector } from "react-redux"

import { apiRequest } from "../../../../../../data/redux/actions/api"
import { isSupersededResponse, pagedRequestKey } from "../../../../../../data/redux/reducers/api/pagedRequest"
import { activitiesService } from "../../../../../../data/services/activitiesService"
import { type FinishedActivitiesParams } from "../../../formatters/activitySearchFormatter"

type ApiSlice = {
    request?: boolean
    success?: boolean
    error?: unknown
    routeParams?: Record<string, unknown>
    urlParams?: Record<string, unknown>
    data?: { meta?: { current_page?: number; last_page?: number; total?: number } }
}

type PageCursor = {
    currentPage: number
    lastPage: number
    totalCount: number
}

const INITIAL_CURSOR: PageCursor = { currentPage: 1, lastPage: 1, totalCount: 0 }

const NO_PARAMS: FinishedActivitiesParams = {}

/**
 * `enabled` (défaut `true`) : faux, rien ne se lit encore — le live ne lit les activités faites
 * suivantes qu'au « Voir plus » de sa feuille.
 */
export function useRealizationsData(
    studentId: string | number | null,
    params: FinishedActivitiesParams = NO_PARAMS,
    { enabled = true }: { enabled?: boolean } = {}
) {
    const dispatch = useDispatch()
    const [cursor, setCursor] = useState<PageCursor>(INITIAL_CURSOR)
    /** Garde le double appel de « Voir plus » avant que le rendu suivant voie `loadingMore`. */
    const loadMoreInFlightRef = useRef(false)

    const req = useSelector(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (state: any) =>
            (studentId != null ? state?.api?.getStudentFinishedActivities : state?.api?.getFinishedActivities) as
                ApiSlice | undefined
    )

    const fetchPage = useCallback(
        (page: number) => {
            if (studentId != null) {
                dispatch(
                    apiRequest(
                        activitiesService.getStudentFinishedActivities,
                        { id: studentId },
                        {},
                        { ...params, page }
                    ) as never
                )
                return
            }
            dispatch(apiRequest(activitiesService.getFinishedActivities, {}, {}, { ...params, page }) as never)
        },
        [dispatch, studentId, params]
    )

    useEffect(() => {
        if (!enabled) return
        setCursor(INITIAL_CURSOR)
        loadMoreInFlightRef.current = false
        fetchPage(1)
    }, [fetchPage, enabled])

    const responseKey = req?.success ? pagedRequestKey(req) : null

    useEffect(() => {
        if (!req?.success) return
        // La réponse d'une recherche dépassée ne fixe pas le curseur (`isSupersededResponse`).
        if (isSupersededResponse(req, studentId != null ? { id: studentId } : {}, params)) return
        const meta = req.data?.meta
        if (meta) {
            setCursor({
                currentPage: meta.current_page ?? 1,
                lastPage: meta.last_page ?? 1,
                totalCount: meta.total ?? 0,
            })
        }
        loadMoreInFlightRef.current = false
        // `meta` est déstructuré en trois primitives : son identité change à chaque réponse et
        // relancerait l'effet pour un curseur inchangé. `responseKey` relance l'effet quand la bonne
        // réponse porte le même curseur qu'une réponse dépassée, écartée juste avant.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [req?.success, responseKey, req?.data?.meta?.current_page, req?.data?.meta?.last_page, req?.data?.meta?.total])

    useEffect(() => {
        if (!req?.error) return
        loadMoreInFlightRef.current = false
        setCursor((previous) => ({ ...previous, lastPage: previous.currentPage }))
    }, [req?.error])

    const hasMore = cursor.currentPage < cursor.lastPage

    const refresh = useCallback(() => {
        loadMoreInFlightRef.current = false
        setCursor(INITIAL_CURSOR)
        fetchPage(1)
    }, [fetchPage])

    const loadMore = useCallback(() => {
        if (loadMoreInFlightRef.current || !!req?.request || !hasMore) return
        loadMoreInFlightRef.current = true
        fetchPage(cursor.currentPage + 1)
    }, [cursor.currentPage, fetchPage, hasMore, req?.request])

    return { refresh, loadMore, hasMore }
}
